import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SkillQuestionTypeEnumLabels } from 'src/app/enums/skill-question-type.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Skill } from 'src/app/models/skill.model';
import { SkillQuestion } from 'src/app/models/skill_question.model';
import { ApiService, UtilsService } from 'src/app/services';

type SkillQuestionRow = SkillQuestion & {
  type_label: string;
  skills_label: string;
  body_excerpt: string;
};

@Component({
  selector: 'app-skill-questions-list',
  templateUrl: './skill-questions-list.component.html',
  styleUrl: './skill-questions-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatOptionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class SkillQuestionsListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);

  readonly skills = signal<Skill[]>([]);

  dataSource = new MatTableDataSource<SkillQuestionRow>([]);

  displayedColumns: string[] = ['type', 'body', 'skills', 'actions'];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
    skill_id: [null as number | null],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadSkills();
    this.listenSearchFormControl();
    this.listenSkillIdFormControl();
  }

  deleteSkillQuestion(skill_question: SkillQuestion): void {
    const excerpt = skill_question.body.length > 60 ? `${skill_question.body.slice(0, 60)}…` : skill_question.body;

    if (!confirm(`¿Eliminar esta pregunta?\n\n${excerpt}`)) {
      return;
    }

    this.api_service
      .deleteSkillQuestion(skill_question.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          const current_page = Number(this.filterForm.get('page')?.value ?? 1);
          if (this.dataSource.data.length === 1 && current_page > 1) {
            this.filterForm.patchValue({ page: current_page - 1 }, { emitEvent: false });
          }
          this.fetchData();
          this.snack_bar.open('Pregunta eliminada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'id:desc',
      with: 'skills',
    };

    if (search_term) {
      params.search = search_term;
    }

    const skill_id = this.filterForm.get('skill_id')?.value as number | null;

    if (skill_id) {
      params.skill_id = skill_id;
    }

    this.api_service
      .getSkillQuestionsPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<SkillQuestion>): void => {
          this.dataSource.data = response.data.map(skill_question => this.mapSkillQuestionRow(skill_question));
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private mapSkillQuestionRow(skill_question: SkillQuestion): SkillQuestionRow {
    const skill_names = skill_question.skills?.map(skill => skill.name) ?? [];
    const body_excerpt = skill_question.body.length > 120 ? `${skill_question.body.slice(0, 120)}…` : skill_question.body;

    return {
      ...skill_question,
      type_label: SkillQuestionTypeEnumLabels[skill_question.type] ?? skill_question.type,
      skills_label: skill_names.length > 0 ? skill_names.join(', ') : '—',
      body_excerpt,
    };
  }

  private loadSkills(): void {
    this.api_service
      .getSkills({ per_page: 500, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<Skill>): void => {
          this.skills.set(response.data);
        },
      });
  }

  private listenSkillIdFormControl(): void {
    this.filterForm
      .get('skill_id')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }
}
